import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, SectionCard, KpiCard } from "@/components/kit";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { fmtDate } from "@/lib/helpers";
import { Plus, Check, X, Trash2, CalendarClock, CalendarDays, Hourglass } from "lucide-react";
import { toast } from "sonner";

const today = () => new Date().toISOString().slice(0, 10);
const emptyLeave = { employee_id: "", from_date: today(), to_date: today(), leave_type: "Casual", reason: "" };
const daysBetween = (a, b) => Math.max(1, Math.round((new Date(b) - new Date(a)) / 86400000) + 1);
const LEAVE_STATUS = {
  Pending: "bg-amber-100 text-amber-800 border-amber-200",
  Approved: "bg-emerald-100 text-emerald-800 border-emerald-200",
  Rejected: "bg-red-100 text-red-700 border-red-200",
};

export default function Leaves() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const isAdmin = ["founder", "admin"].includes(user?.role);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyLeave);
  const [holOpen, setHolOpen] = useState(false);
  const [hol, setHol] = useState({ date: today(), name: "" });

  const { data: leaves = [], isLoading } = useQuery({ queryKey: ["leaves"], queryFn: async () => (await api.get("/leaves")).data });
  const { data: holidays = [] } = useQuery({ queryKey: ["holidays"], queryFn: async () => (await api.get("/holidays")).data });
  const { data: employees = [] } = useQuery({ queryKey: ["employees"], queryFn: async () => (await api.get("/employees")).data });
  const empName = (id) => employees.find((e) => e.employee_id === id)?.name || id;

  const invalidateAll = () => {
    ["leaves", "attendance", "performance", "executive", "management"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
  };

  const apply = useMutation({
    mutationFn: () => api.post("/leaves", { ...form, employee_id: isAdmin ? form.employee_id : undefined }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["leaves"] }); toast.success("Leave request submitted"); setOpen(false); setForm(emptyLeave); },
    onError: () => toast.error("Failed. Select an employee & valid dates."),
  });
  const review = useMutation({
    mutationFn: ({ id, status }) => api.put(`/leaves/${id}/status`, { status }),
    onSuccess: (_, v) => { invalidateAll(); toast.success(`Leave ${v.status.toLowerCase()}`); },
  });
  const delLeave = useMutation({ mutationFn: (id) => api.delete(`/leaves/${id}`), onSuccess: () => { qc.invalidateQueries({ queryKey: ["leaves"] }); toast.success("Deleted"); } });
  const addHol = useMutation({
    mutationFn: () => api.post("/holidays", hol),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["holidays"] }); toast.success("Holiday added"); setHolOpen(false); setHol({ date: today(), name: "" }); },
  });
  const delHol = useMutation({ mutationFn: (id) => api.delete(`/holidays/${id}`), onSuccess: () => { qc.invalidateQueries({ queryKey: ["holidays"] }); toast.success("Removed"); } });

  const pending = leaves.filter((l) => l.status === "Pending").length;
  const approved = leaves.filter((l) => l.status === "Approved").length;

  return (
    <div data-testid="leaves-page">
      <PageHeader title="Leave & Holidays" subtitle="Request leave — approvals flow straight into attendance & performance">
        <Button onClick={() => setOpen(true)} data-testid="apply-leave-btn" className="bg-navy hover:bg-navy-900 text-white rounded-lg"><Plus className="mr-2 h-4 w-4" /> Request Leave</Button>
      </PageHeader>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <KpiCard testId="leave-total" label="Total Requests" value={leaves.length} icon={CalendarClock} />
        <KpiCard testId="leave-pending" label="Pending" value={pending} icon={Hourglass} accent="amber" />
        <KpiCard testId="leave-approved" label="Approved" value={approved} icon={CalendarDays} accent="green" />
        <KpiCard testId="leave-holidays" label="Holidays" value={holidays.length} icon={CalendarDays} accent="gold" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <SectionCard title="Leave Requests" className="lg:col-span-2" testId="leaves-table-card">
          {isLoading ? <Skeleton className="h-48" /> : leaves.length === 0 ? (
            <p className="text-slate-400 text-center py-8">No leave requests yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    {["Employee", "Type", "From", "To", "Days", "Reason", "Status", ""].map((h) => <TableHead key={h} className="text-navy font-semibold">{h}</TableHead>)}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {leaves.map((l) => (
                    <TableRow key={l.leave_id} data-testid={`leave-row-${l.leave_id}`} className="hover:bg-slate-50">
                      <TableCell className="font-medium">{empName(l.employee_id)}</TableCell>
                      <TableCell>{l.leave_type}</TableCell>
                      <TableCell>{fmtDate(l.from_date)}</TableCell>
                      <TableCell>{fmtDate(l.to_date)}</TableCell>
                      <TableCell>{daysBetween(l.from_date, l.to_date)}</TableCell>
                      <TableCell className="max-w-[160px] truncate text-slate-500">{l.reason}</TableCell>
                      <TableCell><Badge variant="outline" className={LEAVE_STATUS[l.status]}>{l.status}</Badge></TableCell>
                      <TableCell className="text-right whitespace-nowrap">
                        {isAdmin && l.status === "Pending" && (
                          <>
                            <Button variant="ghost" size="icon" onClick={() => review.mutate({ id: l.leave_id, status: "Approved" })} data-testid={`approve-${l.leave_id}`}><Check className="h-4 w-4 text-emerald-600" /></Button>
                            <Button variant="ghost" size="icon" onClick={() => review.mutate({ id: l.leave_id, status: "Rejected" })} data-testid={`reject-${l.leave_id}`}><X className="h-4 w-4 text-red-500" /></Button>
                          </>
                        )}
                        {(!isAdmin && l.status === "Pending") && (
                          <Button variant="ghost" size="icon" onClick={() => delLeave.mutate(l.leave_id)}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </SectionCard>

        <SectionCard
          title="Company Holidays"
          testId="holidays-card"
          action={isAdmin && <Button size="sm" variant="outline" onClick={() => setHolOpen(true)} data-testid="add-holiday-btn" className="rounded-lg"><Plus className="mr-1 h-3.5 w-3.5" /> Add</Button>}
        >
          {holidays.length === 0 ? <p className="text-slate-400 text-center py-6">No holidays listed.</p> : (
            <div className="space-y-3">
              {holidays.map((h) => (
                <div key={h.holiday_id} className="flex items-center justify-between rounded-lg border border-slate-200 p-3" data-testid={`holiday-${h.holiday_id}`}>
                  <div className="flex items-center gap-3">
                    <div className="rounded-md bg-gold/15 p-2"><CalendarDays className="h-4 w-4 text-gold-dark" /></div>
                    <div>
                      <div className="text-sm font-semibold text-navy">{h.name}</div>
                      <div className="text-xs text-slate-500">{fmtDate(h.date)}</div>
                    </div>
                  </div>
                  {isAdmin && <Button variant="ghost" size="icon" onClick={() => delHol.mutate(h.holiday_id)}><Trash2 className="h-4 w-4 text-red-400" /></Button>}
                </div>
              ))}
            </div>
          )}
        </SectionCard>
      </div>

      {/* Request Leave dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="font-heading text-navy">Request Leave</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            {isAdmin && (
              <div className="col-span-2">
                <Label>Employee</Label>
                <Select value={form.employee_id} onValueChange={(v) => setForm({ ...form, employee_id: v })}>
                  <SelectTrigger data-testid="leave-employee-select"><SelectValue placeholder="Select employee" /></SelectTrigger>
                  <SelectContent>{employees.map((e) => <SelectItem key={e.employee_id} value={e.employee_id}>{e.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label>Leave Type</Label>
              <Select value={form.leave_type} onValueChange={(v) => setForm({ ...form, leave_type: v })}>
                <SelectTrigger data-testid="leave-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Casual">Casual</SelectItem>
                  <SelectItem value="Sick">Sick</SelectItem>
                  <SelectItem value="Earned">Earned</SelectItem>
                  <SelectItem value="Unpaid">Unpaid</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div />
            <div><Label>From</Label><Input type="date" data-testid="leave-from-input" value={form.from_date} onChange={(e) => setForm({ ...form, from_date: e.target.value })} /></div>
            <div><Label>To</Label><Input type="date" data-testid="leave-to-input" value={form.to_date} onChange={(e) => setForm({ ...form, to_date: e.target.value })} /></div>
            <div className="col-span-2"><Label>Reason</Label><Textarea data-testid="leave-reason-input" value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} /></div>
          </div>
          <DialogFooter><Button onClick={() => apply.mutate()} data-testid="submit-leave-btn" className="bg-navy text-white">Submit Request</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Holiday dialog */}
      <Dialog open={holOpen} onOpenChange={setHolOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-heading text-navy">Add Company Holiday</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Date</Label><Input type="date" value={hol.date} onChange={(e) => setHol({ ...hol, date: e.target.value })} /></div>
            <div><Label>Holiday Name</Label><Input data-testid="holiday-name-input" value={hol.name} onChange={(e) => setHol({ ...hol, name: e.target.value })} /></div>
          </div>
          <DialogFooter><Button onClick={() => addHol.mutate()} data-testid="save-holiday-btn" className="bg-navy text-white">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
